import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { useForm, useWatch, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { pipe, Option, Array as A } from 'effect';

import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ReceiptOutlinedIcon from '@mui/icons-material/ReceiptOutlined';
import CheckIcon from '@mui/icons-material/Check';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';

import DialogBase from '@/components/mui/Dialog';
import CircularProgressWithBackdrop from '@/components/mui/CircularProgressWithBackdrop';
const Dialog = DialogBase as React.ComponentType<any>;

import { useBeneficiaryForm, useBeneficiaryDetails, useCreateBeneficiaryMutation } from '@/domain/payments/queries';
import { firstActivePaymentProviderNameSelector } from '@/domain/paySettings';

import {
  VendorHeader,
  FormContent,
  Subtitle,
  VendorInfoRow,
  VendorLabel,
  AccountsChip,
  DefaultToggleBox,
  DefaultLabelContent,
  DefaultStarIcon,
  DefaultFormControlLabel,
  FormActions,
  SaveNewBeneficiaryBox,
} from '@/pages/dokkaPay/pages/payment/components/PaymentDetails/components/PaymentInfo/components/BeneficiaryModal/StyledComponents';
import {
  buildZodSchema,
  buildDefaultValues,
  getVisibleFields,
  getParentFieldName,
  pickVisibleValues,
} from '@/pages/dokkaPay/pages/payment/components/PaymentDetails/components/PaymentInfo/components/BeneficiaryModal/utils';
import BeneficiaryFields from '@/pages/dokkaPay/pages/payment/components/PaymentDetails/components/PaymentInfo/components/BeneficiaryModal/BeneficiaryFields';
import { BENEFICIARY_SOURCE } from '@/pages/dokkaPay/pages/payment/components/PaymentDetails/components/PaymentInfo/components/BeneficiaryModal/types';
import type { BeneficiaryModalProps, BeneficiarySource, FormValues } from '@/pages/dokkaPay/pages/payment/components/PaymentDetails/components/PaymentInfo/components/BeneficiaryModal/types';

const BeneficiaryModal: React.FC<BeneficiaryModalProps> = ({
  onClose,
  onSubmit,
  vendorId,
  vendorName,
  existingAccountsCount,
  beneficiaryId,
  isEditable = false,
  isDefault = false,
  isExternal = false,
  withDefaultToggle = true,
  withPaymentScopedEdit = false,
  onBeneficiaryCreated,
  isBeneficiaryListRefreshing = false,
  initialDetails,
  isLocalBeneficiary = false,
}) => {
  const { formatMessage } = useIntl();
  const providerType = useSelector(firstActivePaymentProviderNameSelector);

  const isNewBeneficiary = !beneficiaryId || isExternal;

  // Only meaningful on surfaces that render the toggle (`withDefaultToggle`), i.e.
  // the beneficiaries grid in settings: a vendor's first account has nothing to
  // compete with, so the toggle is shown on and locked rather than letting the user
  // create a lone account that no Pay request would pick up. The payment flow renders
  // no toggle at all and sends no flag, so this value goes unused there and the
  // vendor's first account is made default by the backend instead (DPM-661).
  const isForcedDefault = isNewBeneficiary && existingAccountsCount === 0;

  // When an ERP-sourced beneficiary is missing required fields, the user can opt to
  // create a brand-new (user-owned, non-synced) beneficiary based on the same data.
  const [createFromErp, setCreateFromErp] = useState(false);

  const { data, isLoading } = useBeneficiaryForm();
  const createBeneficiary = useCreateBeneficiaryMutation();

  // The created beneficiary plus the values it came from, so "stored and unchanged
  // since" is derived rather than tracked and cannot drift from the payload choice.
  const [savedBeneficiary, setSavedBeneficiary] = useState<{ id: string; valuesKey: string } | null>(null);

  // NOTE: isInitialLoading, NOT isLoading. For a NEW beneficiary the details query
  // is disabled (enabled: !!id with an empty id) for the modal's whole lifetime,
  // and in TanStack Query *v4* a disabled query stays status:'loading' => isLoading:true
  // (v5 redefined isLoading as isPending && isFetching; v4 did not). Reading isLoading
  // here would pin the modal's backdrop spinner. isInitialLoading (= isLoading &&
  // isFetching) is false while disabled and true only during the real first fetch
  // (edit case). This was an observed bug, not theoretical.
  // An empty id disables the query. `beneficiaryId` still has to be passed — it is
  // what marks this as an existing beneficiary rather than a new one.
  const { data: fetchedDetails, isInitialLoading: detailsLoading } = useBeneficiaryDetails(
    isLocalBeneficiary ? '' : (beneficiaryId ?? ''),
    isExternal,
  );

  const resolvedInitialValues = initialDetails ?? fetchedDetails?.details;

  // The schema is rebuilt whenever the visible field set changes (a `visibleWhen`
  // discriminator toggles branches). useForm's resolver reads the latest schema
  // from a ref so validation always matches what is currently rendered.
  const schemaRef = useRef<z.ZodTypeAny>(z.object({}));
  const resolver = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (values: any, context: any, options: any) => (zodResolver(schemaRef.current as never) as any)(values, context, options),
    [],
  );

  const {
    register,
    control,
    handleSubmit,
    reset,
    trigger,
    setValue,
    formState: { errors, isSubmitting, isValid, touchedFields, dirtyFields },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } = useForm<FormValues>({ mode: 'onTouched', resolver: resolver as any });

  // Only fields whose `visibleWhen` guard passes are rendered, validated and
  // submitted. Watch the whole form so any field can act as a discriminator.
  const watchedValues = useWatch({ control }) as FormValues;
  const visibleFields = useMemo(
    () => (data?.fields ? getVisibleFields(data.fields, watchedValues) : []),
    [data, watchedValues],
  );
  const visibilityKey = visibleFields.map((f) => f.name).join('|');
  const schema = useMemo(
    () => buildZodSchema(visibleFields, formatMessage),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visibilityKey, data],
  );
  schemaRef.current = schema;

  const contentLoading = isLoading || detailsLoading;

  const didInitRef = useRef(false);
  useEffect(() => {
    if (!data?.fields?.length) return;
    // Wait for any load that is actually running. Not gated on `isNewBeneficiary`: an
    // ERP-sourced row counts as new yet still fetches, so gating on it would init with
    // blank defaults and the guard below would then refuse the arriving details. A
    // disabled query reports `isInitialLoading: false`, so a genuinely new beneficiary
    // does not wait.
    if (detailsLoading) return;
    // Once only: the query refetches on window focus, and changed data would re-run
    // `reset` and discard whatever the user has typed.
    if (didInitRef.current) return;
    const effectiveIsDefault = isForcedDefault || (fetchedDetails?.isDefault ?? isDefault);
    reset({
      ...buildDefaultValues(data.fields, resolvedInitialValues),
      __isDefault: effectiveIsDefault ? 'true' : 'false',
    });
    trigger();
    didInitRef.current = true;
  }, [
    data,
    resolvedInitialValues,
    isNewBeneficiary,
    detailsLoading,
    reset,
    trigger,
    fetchedDetails?.isDefault,
    isDefault,
    isForcedDefault,
  ]);

  // Whenever the visible branch changes: (1) on a GENUINE user switch of a
  // `visibleWhen` discriminator, clear any field whose name is reused across
  // branches so a stale value can't bleed over — this must NOT fire on the initial branch
  // establishment (mount / edit-prefill), so we skip when the discriminator's
  // previous value was empty; (2) ALWAYS revalidate against the now-settled
  // schema so `isValid` reflects the newly-rendered field set rather than riding
  // on RHF's implicit re-runs.
  const prevDiscriminatorsRef = useRef<Record<string, string> | null>(null);
  useEffect(() => {
    if (!didInitRef.current || !data?.fields?.length) return;
    const discriminatorNames = Array.from(
      new Set(data.fields.map((f) => f.visibleWhen?.field).filter((n): n is string => !!n)),
    );
    const current = Object.fromEntries(
      discriminatorNames.map((n) => [n, (watchedValues[n] as string) ?? '']),
    );
    const prev = prevDiscriminatorsRef.current;
    prevDiscriminatorsRef.current = current;
    const isUserToggle = !!prev && discriminatorNames.some((n) => prev[n] && prev[n] !== current[n]);

    if (isUserToggle) {
      const conditional = data.fields.filter((f) => f.visibleWhen);
      const nameCounts = conditional.reduce<Record<string, number>>((acc, f) => {
        acc[f.name] = (acc[f.name] ?? 0) + 1;
        return acc;
      }, {});
      conditional.forEach((f) => {
        if (nameCounts[f.name] > 1) setValue(f.name, '');
      });
    }
    trigger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibilityKey]);

  // A field whose options are scoped to a parent must not keep a value that
  // belonged to a different parent: the same branch code exists under several
  // banks, so a stale bank/branch pair would submit a plausible-looking but
  // wrong account. Keyed on the PARENT VALUE changing — not on the option list,
  // whose identity changes on every render.
  const dependentPairs = useMemo(
    () =>
      pipe(
        data?.fields ?? [],
        A.filterMap((f) =>
          pipe(
            Option.fromNullable(getParentFieldName(f)),
            Option.map((parent) => ({ child: f.name, parent })),
          ),
        ),
      ),
    [data],
  );
  // Clear every field scoped to `parentName` the moment that parent commits a new
  // value. Deliberately NOT a watch-based effect: an effect runs after the render
  // that has already re-resolved the child against its new option list, and
  // because branch codes repeat across banks the child silently re-resolves to a
  // different branch with the same code instead of clearing.
  const clearDependentsOf = useCallback(
    (parentName: string) => {
      const children = pipe(
        dependentPairs,
        A.filter(({ parent }) => parent === parentName),
      );
      if (A.isEmptyReadonlyArray(children)) return;

      children.forEach(({ child }) => setValue(child, '', { shouldValidate: false }));
      // Revalidate so the emptied child reports as required rather than staying
      // silently valid from its previous value.
      trigger();
    },
    [dependentPairs, setValue, trigger],
  );

  const isErpIncomplete = fetchedDetails?.source === BENEFICIARY_SOURCE.erp && !isValid;
  // Show the warning (and lock the fields) until the user chooses to create a new beneficiary.
  const showIncompleteErpAlert = isErpIncomplete && !createFromErp;
  const readOnly = showIncompleteErpAlert;
  // Once the user opts in, treat the flow as a fresh beneficiary creation.
  const effectiveIsNew = isNewBeneficiary || createFromErp;

  const hasDirtyFields = Object.keys(dirtyFields).length > 0;
  const hasDetailsDirty = Object.keys(dirtyFields).some((key) => key !== '__isDefault');

  // Hidden conditional inputs must not make two identical forms look different.
  const visibleValuesKey = useMemo(
    () => JSON.stringify(pickVisibleValues(watchedValues, visibleFields)),
    [watchedValues, visibleFields],
  );
  const savedBeneficiaryId =
    savedBeneficiary?.valuesKey === visibleValuesKey ? (savedBeneficiary?.id ?? null) : null;

  // Not done until the list the payment offers holds the new beneficiary, so whatever
  // the user does next acts on a list that includes it. A failed re-read clears the
  // flag just the same — the beneficiary exists either way. The mutation also outlives
  // the dialog, so dismissing mid-flight would leave nothing to report it on.
  const isStoring = createBeneficiary.isLoading || isBeneficiaryListRefreshing;
  const handleClose = useCallback(() => {
    // `isSubmitting` too: only the dismiss button is disabled for it, while the title
    // button, Escape and a backdrop click all arrive here.
    if (isStoring || isSubmitting) return;
    onClose();
  }, [isStoring, isSubmitting, onClose]);

  const handleSaveAsNewBeneficiary = useCallback(() => {
    const details = { ...pickVisibleValues(watchedValues, visibleFields), type: providerType };
    createBeneficiary.mutate(
      // No default-account flag: a payment-flow save expresses no opinion on which
      // account is the vendor's default (DPM-661).
      { body: { details, vendorId, source: BENEFICIARY_SOURCE.user } },
      {
        onSuccess: (created) => {
          setSavedBeneficiary({ id: created.id, valuesKey: visibleValuesKey });
          onBeneficiaryCreated?.();
        },
      },
    );
  }, [
    createBeneficiary,
    watchedValues,
    visibleFields,
    providerType,
    vendorId,
    visibleValuesKey,
    onBeneficiaryCreated,
  ]);

  const onFormSubmit = useCallback(
    async ({ __isDefault, ...fields }: FormValues) => {
      // Payload carries only the currently-visible fields (hidden conditional
      // inputs guarded by `visibleWhen` must not leak in).
      const details = { ...pickVisibleValues(fields, visibleFields), type: providerType };
      const source: BeneficiarySource = hasDetailsDirty ? BENEFICIARY_SOURCE.user : fetchedDetails?.source ?? BENEFICIARY_SOURCE.user;
      // A surface that renders no toggle expresses no opinion on the default account,
      // so the flag is left out of the payload entirely rather than sent as false.
      const defaultFlag = withDefaultToggle ? { isDefault: __isDefault === 'true' } : {};

      // Either branch leaves the vendor's existing records untouched (DPM-751).
      const submitData: Record<string, unknown> = effectiveIsNew
        ? { details, isNewBeneficiary: true, vendorId, ...defaultFlag, source }
        : withPaymentScopedEdit
          ? { payTo: savedBeneficiaryId ?? details }
          : { details, ...defaultFlag, source };

      // Close only on success. A rejected onSubmit (e.g. duplicate-beneficiary 400)
      // keeps the modal open so the user doesn't lose input; the error toast is
      // surfaced by the mutation hook's onError.
      try {
        await onSubmit(submitData);
        onClose();
      } catch {
        // keep the modal open
      }
    },
    [effectiveIsNew, withPaymentScopedEdit, savedBeneficiaryId, hasDetailsDirty, withDefaultToggle, vendorId, providerType, fetchedDetails, visibleFields, onSubmit, onClose],
  );

  const title = effectiveIsNew
    ? formatMessage({ id: 'beneficiary.modal.add.title', defaultMessage: 'Add New Beneficiary' })
    : withPaymentScopedEdit
      ? formatMessage({ id: 'beneficiary.modal.paymentEdit.title', defaultMessage: 'Edit Beneficiary Details' })
      : isEditable
        ? formatMessage({ id: 'beneficiary.modal.edit.title', defaultMessage: 'Edit Beneficiary' })
        : formatMessage({ id: 'beneficiary.modal.view.title', defaultMessage: 'Beneficiary Details' });

  const subtitle = effectiveIsNew
    ? formatMessage({ id: 'beneficiary.modal.add.subtitle', defaultMessage: 'New bank account for payment' })
    : withPaymentScopedEdit
      ? formatMessage({
          id: 'beneficiary.modal.paymentEdit.subtitle',
          defaultMessage: 'Editing banking details for this payment',
        })
      : isEditable
        ? formatMessage({ id: 'beneficiary.modal.edit.subtitle', defaultMessage: 'Update bank account details' })
        : formatMessage({ id: 'beneficiary.modal.view.subtitle', defaultMessage: 'Bank account details' });

  if (contentLoading) {
    return <CircularProgressWithBackdrop />;
  }
  return (
    <Dialog
      open
      onClose={handleClose}
      maxWidth="sm"
      withCloseBtn
      withContent={false}
      withActions={false}
      title={title}
    >
      <form onSubmit={handleSubmit(onFormSubmit)}>
        <FormContent>
          <Subtitle variant="body2" color="text.secondary">
            {subtitle}
          </Subtitle>

          <VendorHeader>
            <VendorInfoRow>
              <ReceiptOutlinedIcon color="primary" fontSize="medium" />
              <Box>
                <VendorLabel variant="subtitle2" color="text.secondary">
                  {formatMessage({ id: 'beneficiary.modal.vendorFor', defaultMessage: 'Beneficiary for' })}
                </VendorLabel>
                <Typography variant="subtitle2">
                  {vendorName}
                </Typography>
              </Box>
            </VendorInfoRow>
            {/* First beneficiary for this vendor → nothing to count, no chip. */}
            {existingAccountsCount > 0 && (
              <AccountsChip
                icon={<AccountBalanceIcon />}
                label={formatMessage(
                  {
                    id: 'beneficiary.modal.existingAccounts',
                    defaultMessage: 'Vendor has {count} more {count, plural, one {account} other {accounts}}',
                  },
                  { count: existingAccountsCount },
                )}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
          </VendorHeader>

          {showIncompleteErpAlert && (
            <Alert severity="warning">
              <AlertTitle>
                {formatMessage({
                  id: 'beneficiary.modal.erpIncomplete.title',
                  defaultMessage: 'Missing required fields',
                })}
              </AlertTitle>
              {formatMessage({
                id: 'beneficiary.modal.erpIncomplete.description',
                defaultMessage:
                  'This beneficiary comes from your ERP but is missing required fields needed to create it in DOKKA-PAY. Complete the missing fields in your ERP, or create a new beneficiary based on the existing data. Note: a beneficiary created here will not be synced with your ERP.',
              })}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
                <Button
                  color="warning"
                  size="small"
                  variant="outlined"
                  onClick={() => setCreateFromErp(true)}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {formatMessage({
                    id: 'beneficiary.modal.erpIncomplete.action',
                    defaultMessage: 'Create new from this data',
                  })}
                </Button>
              </Box>
            </Alert>
          )}

          {data?.fields && (
            <BeneficiaryFields
              fields={visibleFields}
              register={register}
              control={control}
              setValue={setValue}
              errors={errors}
              touchedFields={touchedFields}
              isSubmitting={isSubmitting}
              readOnly={readOnly}
              values={watchedValues}
              onValueCommitted={clearDependentsOf}
            />
          )}

          {withPaymentScopedEdit && !effectiveIsNew && (
            <SaveNewBeneficiaryBox>
              <SaveOutlinedIcon fontSize="small" color="action" sx={{ mt: 0.25 }} />
              <Box flex={1} minWidth={0}>
                <Typography variant="body2" fontWeight={500}>
                  {formatMessage({
                    id: 'beneficiary.modal.saveNew.title',
                    defaultMessage: 'Save new banking details for this beneficiary',
                  })}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {formatMessage(
                    {
                      id: 'beneficiary.modal.saveNew.description',
                      defaultMessage:
                        'This payment will use the new details. Save them to {vendorName} beneficiary record so future payments use these details too.',
                    },
                    { vendorName },
                  )}
                </Typography>
                <Box sx={{ mt: 1.5 }}>
                  {savedBeneficiaryId && !isStoring ? (
                    // A status, not a control: a disabled button is announced as an
                    // unavailable action, and any button — even `component="div"` —
                    // stays focusable through ButtonBase. A plain Chip is neither.
                    <Chip
                      role="status"
                      size="small"
                      variant="outlined"
                      color="success"
                      icon={<CheckIcon />}
                      label={formatMessage({
                        id: 'beneficiary.modal.saveNew.done',
                        defaultMessage: 'Saved to beneficiary',
                      })}
                      // Chip does not uppercase its label the way Button does, and this
                      // sits where a button was — so it borrows the button's casing,
                      // weight and tracking to stay of a piece with the one beside it.
                      // Text and border on `success.main`, tint on `success.light`: the
                      // light tone on the tint measures about 3.1:1, under the 4.5:1
                      // AA needs at this size. The tint is what keeps it from reading
                      // as heavily as a plain dark-green outline.
                      sx={(theme) => ({
                        color: theme.palette.success.main,
                        borderColor: theme.palette.success.main,
                        backgroundColor: alpha(theme.palette.success.light, 0.12),
                        textTransform: 'uppercase',
                        fontWeight: 500,
                        letterSpacing: '0.02857em',
                        '& .MuiChip-icon': { color: 'inherit' },
                      })}
                    />
                  ) : (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleSaveAsNewBeneficiary}
                      loading={isStoring}
                      disabled={!isValid || !hasDirtyFields || isSubmitting || readOnly}
                    >
                      {formatMessage({
                        id: 'beneficiary.modal.saveNew.action',
                        defaultMessage: 'Save new beneficiary',
                      })}
                    </Button>
                  )}
                </Box>
              </Box>
            </SaveNewBeneficiaryBox>
          )}

          {withDefaultToggle && (
            <DefaultToggleBox>
              <Controller
                name="__isDefault"
                control={control}
                defaultValue={isForcedDefault || isDefault ? 'true' : 'false'}
                render={({ field: f }) => (
                  <DefaultFormControlLabel
                    control={
                      <Switch
                        checked={f.value === 'true'}
                        onChange={(e) => f.onChange(e.target.checked ? 'true' : 'false')}
                        disabled={isSubmitting || readOnly || isForcedDefault}
                        size="small"
                      />
                    }
                    label={
                      <DefaultLabelContent>
                        <DefaultStarIcon fontSize="small" active={f.value === 'true'} />
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {formatMessage({
                              id: 'beneficiary.modal.setDefault',
                              defaultMessage: 'Set as default beneficiary for this vendor',
                            })}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatMessage({
                              id: 'beneficiary.modal.setDefault.description',
                              defaultMessage:
                                'Will replace the current beneficiary default. Any new Pay request will use this beneficiary.',
                            })}
                          </Typography>
                        </Box>
                      </DefaultLabelContent>
                    }
                    labelPlacement="start"
                  />
                )}
              />
            </DefaultToggleBox>
          )}
        </FormContent>

        <FormActions>
          <Button variant="outlined" onClick={handleClose} disabled={isSubmitting || isStoring}>
            {formatMessage({ id: 'button.cancel', defaultMessage: 'Cancel' })}
          </Button>
          <Button
            type="submit"
            variant="contained"
            loading={isSubmitting}
            disabled={!isValid || (!effectiveIsNew && !hasDirtyFields) || isStoring}
          >
            {effectiveIsNew
              ? formatMessage({ id: 'beneficiary.modal.submit.add', defaultMessage: 'Add Beneficiary' })
              : withPaymentScopedEdit
                ? formatMessage({ id: 'beneficiary.modal.submit.updatePayment', defaultMessage: 'Update payment' })
                : formatMessage({ id: 'beneficiary.modal.submit.update', defaultMessage: 'Update' })}
          </Button>
        </FormActions>
      </form>
    </Dialog>
  );
};

export default BeneficiaryModal;
